import React, { useState, useRef } from 'react';
import { captureException } from '@sentry/react';
import toast from 'react-hot-toast';
import {
  FileIcon,
  UploadIcon,
  DownloadSimpleIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XIcon,
  PlusIcon,
} from '@phosphor-icons/react';

import { Button } from '../../../../../components/button/Button';
import { Input } from '../../../../../components/input/Input';
import { InputWrapper } from '../../../../../components/InputWrapper';
import { ConfirmDialog } from '../../../../../components/dialog/ConfirmDialog';
import { getDisplayError } from '@utils/get-display-error';
import { formatDate } from '@utils/date';
import {
  linkCarrierInformationDocument,
  unlinkCarrierInformationDocument,
  updateCarrierInformationDocument,
} from '../../../models';
import { useDownloadDocument } from '../../../../document/hooks/useDownloadDocument';

const getFileNameWithoutExtension = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
};

interface DocumentCardProps {
  mode: 'upload' | 'existing';
  // For upload mode
  onUploadSuccess?: () => void; // Callback to refresh the parent list
  // For existing mode
  document?: {
    id: string;
    name: string;
    hash: string;
    createdAt: string;
  };
  attachmentId?: string; // The CarrierInformationDocument ID for unlinking
  onDeleteSuccess?: () => void; // Callback to refresh the parent list
  onRenameSuccess?: () => void; // Callback to refresh the parent list
}

export const DocumentCard: React.FC<DocumentCardProps> = ({
  mode,
  onUploadSuccess,
  document,
  attachmentId,
  onDeleteSuccess,
  onRenameSuccess,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [selectedFile, setSelectedFile] = useState<globalThis.File | null>(null);
  const [customName, setCustomName] = useState('');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { downloadFile, downloadingFiles } = useDownloadDocument();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setCustomName(getFileNameWithoutExtension(file.name));
      setUploadError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.set('name', selectedFile.name);
      formData.set('file', selectedFile);

      const result = await fetch('/api/document/upload', {
        body: formData,
        method: 'post',
      });

      const text = await result.text();
      if (!result.ok) {
        console.error(text);
        throw new Error('Upload mislukt');
      }

      const uploadedDoc = JSON.parse(text).data;

      // Link the document to carrier information
      const finalFileName = customName.trim() || selectedFile.name;
      await linkCarrierInformationDocument({
        documentId: uploadedDoc.id,
        name: finalFileName,
      });

      toast.success(`Document "${finalFileName}" succesvol opgeladen`);

      // Reset the form
      setSelectedFile(null);
      setCustomName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Notify parent to refresh the list
      onUploadSuccess?.();
    } catch (err) {
      captureException(err);
      const errorMessage = getDisplayError(err);
      setUploadError(errorMessage);
      toast.error(`Kon document niet opladen "${selectedFile.name}": ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = () => {
    if (document) {
      downloadFile(document);
    }
  };

  const handleDelete = async () => {
    if (!document || !attachmentId) return;

    setIsDeleting(true);

    try {
      await unlinkCarrierInformationDocument(attachmentId);

      toast.success('Document verwijderd');
      onDeleteSuccess?.();
    } catch (err) {
      captureException(err);
      toast.error(`Kon document niet verwijderen: ${getDisplayError(err)}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRename = async (newName: string) => {
    if (!document || !newName.trim()) return;

    setIsRenaming(true);

    try {
      await updateCarrierInformationDocument(document.id, newName.trim());
      toast.success('Naam van document is aangepast');
      onRenameSuccess?.();
    } catch (err) {
      captureException(err);
      toast.error(`Kon naam van document niet aanpassen: ${getDisplayError(err)}`);
    } finally {
      setIsRenaming(false);
    }
  };

  const startEdit = () => {
    if (document) {
      setEditName(document.name);
      setIsEditing(true);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditName('');
  };

  const saveEdit = async () => {
    if (!editName.trim()) {
      cancelEdit();
      return;
    }

    await handleRename(editName.trim());
    setIsEditing(false);
    setEditName('');
  };

  const clearFile = () => {
    setSelectedFile(null);
    setCustomName('');
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload mode card
  if (mode === 'upload') {
    return (
      <div className="bg-surface border border-dashed border-border rounded-lg p-4 hover:border-border-strong transition-colors">
        {!selectedFile ? (
          // File selection state
          <div className="text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="cursor-pointer py-4" onClick={() => fileInputRef.current?.click()}>
              <PlusIcon className="mx-auto h-8 w-8 text-fg-muted" />
              <div className="mt-2 text-sm font-medium text-fg">Document uploaden</div>
              <div className="mt-1 text-sm text-fg-muted">Klik om een PDF-bestand te selecteren</div>
            </div>
          </div>
        ) : (
          // File selected state
          <div>
            <div className="flex items-start gap-2">
              <FileIcon className="w-5 h-5 text-fg-muted mt-1 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-fg truncate">{selectedFile.name}</div>
                <div className="text-xs text-fg-muted">{Math.round(selectedFile.size / 1024)} KB</div>
              </div>
              <Button color="ghost" ariaLabel="Selectie wissen" onTrigger={clearFile} isDisabled={isUploading}>
                <XIcon className="w-4 h-4" />
              </Button>
            </div>

            <div className="mt-2">
              <InputWrapper labelText="Aangepaste naam (optioneel)">
                <Input
                  type="text"
                  value={customName}
                  onChange={setCustomName}
                  placeholder={selectedFile.name}
                  isDisabled={isUploading}
                />
              </InputWrapper>
            </div>

            {uploadError && <div className="mb-2 text-sm text-negative-text">{uploadError}</div>}

            <div className="flex justify-end">
              <Button
                color="primary"
                onTrigger={handleUpload}
                isLoading={isUploading}
                isDisabled={isUploading}
                iconLeft={<UploadIcon className="w-4 h-4" />}
              >
                Uploaden
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Existing document mode card
  if (mode === 'existing' && document) {
    const isDownloading = downloadingFiles.includes(document.id);

    return (
      <div className="rounded-lg border border-border bg-surface p-4">
        <div className="flex items-start gap-2">
          <FileIcon className="w-5 h-5 text-fg-muted mt-1 shrink-0" />

          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div>
                <Input
                  type="text"
                  value={editName}
                  onChange={setEditName}
                  placeholder="Voer documentnaam in"
                  isDisabled={isRenaming}
                />
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    color="primary"
                    onTrigger={saveEdit}
                    iconLeft={<CheckIcon className="w-4 h-4" />}
                    isLoading={isRenaming}
                    isDisabled={isRenaming}
                  >
                    Opslaan
                  </Button>
                  <Button color="ghost" onTrigger={cancelEdit} isDisabled={isRenaming}>
                    Annuleren
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="text-sm font-medium text-fg truncate">{document.name}</div>
                <div className="text-xs text-fg-muted mt-1">Geupload op {formatDate(document.createdAt)}</div>
              </>
            )}
          </div>

          {!isEditing && (
            <div className="flex items-center shrink-0">
              <Button
                color="ghost"
                ariaLabel="Downloaden"
                onTrigger={handleDownload}
                isLoading={isDownloading}
                isDisabled={isDeleting}
              >
                <DownloadSimpleIcon className="w-4 h-4" />
              </Button>

              <Button color="ghost" ariaLabel="Hernoemen" onTrigger={startEdit} isDisabled={isDeleting}>
                <PencilIcon className="w-4 h-4" />
              </Button>

              <ConfirmDialog
                triggerColor="ghost"
                triggerText={<TrashIcon className="w-4 h-4 text-negative-text" />}
                title="Document verwijderen"
                submitText="Verwijderen"
                description={`Weet u zeker dat u "${document.name}" wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`}
                onSubmit={handleDelete}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};
